// To update the data displayed by this component, see /scripts/instructions.txt

import React, { useState, useMemo } from 'react';
import Select from '../../../Inputs/Select/Select';
import ThreadsTableGroup from 'plaid-threads/TableGroup';
import { Box } from 'src/components-v2/Layout';
import styles from './index.module.scss';
import Image from '../../../Image';
import Tooltip from 'plaid-threads/Tooltip';
import ThreadsSearchInput from 'threads5/SearchInput';

interface Institution {
  id: string;
  institution_name: string;
  brand_name: string;
  country_code: string;
  payment: string;
  identity: string;
  data: string;
  personal: string;
  business: string;
  url: string;
}

interface Brand {
  brand_name: string;
  institutions: Institution[];
  country_code: string;
  payment: string;
  identity: string;
  data: string;
  personal: string;
  business: string;
}

const SOLUTION_OPTIONS = [
  { label: 'All solutions', value: 'all' },
  { label: 'Payments', value: 'payment' },
  { label: 'Identity', value: 'identity' },
  { label: 'Account Data', value: 'data' },
];

const COUNTRY_NAMES: Record<string, string> = {
  AT: 'Austria',
  BE: 'Belgium',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  IT: 'Italy',
  IE: 'Ireland',
  LV: 'Latvia',
  LT: 'Lithuania',
  NL: 'Netherlands',
  NO: 'Norway',
  PL: 'Poland',
  PT: 'Portugal',
  ES: 'Spain',
  SE: 'Sweden',
  GB: 'United Kingdom',
};

const COUNTRY_FLAGS: Record<string, string> = {
  AT: '\u{1F1E6}\u{1F1F9}',
  BE: '\u{1F1E7}\u{1F1EA}',
  DK: '\u{1F1E9}\u{1F1F0}',
  EE: '\u{1F1EA}\u{1F1EA}',
  FI: '\u{1F1EB}\u{1F1EE}',
  FR: '\u{1F1EB}\u{1F1F7}',
  DE: '\u{1F1E9}\u{1F1EA}',
  IT: '\u{1F1EE}\u{1F1F9}',
  IE: '\u{1F1EE}\u{1F1EA}',
  LV: '\u{1F1F1}\u{1F1FB}',
  LT: '\u{1F1F1}\u{1F1F9}',
  NO: '\u{1F1F3}\u{1F1F4}',
  PL: '\u{1F1F5}\u{1F1F1}',
  PT: '\u{1F1F5}\u{1F1F9}',
  ES: '\u{1F1EA}\u{1F1F8}',
  SE: '\u{1F1F8}\u{1F1EA}',
  NL: '\u{1F1F3}\u{1F1F1}',
  GB: '\u{1F1EC}\u{1F1E7}',
};

type Solution = 'all' | 'payment' | 'identity' | 'data';

// Define order: Business, Personal, Personal / Business, -
const accountTypeSortingOrder = {
  Business: 1,
  Personal: 2,
  'Personal / Business': 3,
  '-': 4,
};

const parseCSV = (csv: string): Brand[] => {
  // Skip header row and split into lines
  const rows = csv.trim().split('\n').slice(1);

  const institutionsList = rows.map((row) => {
    //id,brand_name,institution_name,country_code,payment,identity,data,url,personal,business
    const [
      id,
      brand_name,
      institution_name,
      country_code,
      payment,
      identity,
      data,
      url = '',
      personal,
      business,
    ] = row.split(',').map((item) => item.trim());

    return {
      id,
      brand_name,
      institution_name,
      country_code,
      payment,
      identity,
      data,
      personal,
      business,
      url,
    };
  });

  const brands = {};
  institutionsList.forEach((institution) => {
    const brandKey = institution.brand_name + institution.country_code;
    if (!brands[brandKey]) {
      brands[brandKey] = {
        brand_name: institution.brand_name,
        country_code: institution.country_code,
        institutions: [],
        payment: institution.payment,
        identity: institution.identity,
        data: institution.data,
        personal: institution.personal,
        business: institution.business,
      };
    }
    brands[brandKey].institutions.push(institution);

    // Full support ('1') always wins over beta; beta wins over none.
    const mergeProduct = (current: string, incoming: string) => {
      if (current === '1' || incoming === '1') return '1';
      if (current === 'beta' || incoming === 'beta') return 'beta';
      return current;
    };
    brands[brandKey].payment = mergeProduct(
      brands[brandKey].payment,
      institution.payment,
    );
    brands[brandKey].identity = mergeProduct(
      brands[brandKey].identity,
      institution.identity,
    );
    brands[brandKey].data = mergeProduct(
      brands[brandKey].data,
      institution.data,
    );
    if (institution.personal === '1') {
      brands[brandKey].personal = '1';
    }
    if (institution.business === '1') {
      brands[brandKey].business = '1';
    }
  });

  return Object.values(brands);
};

const renderProductCell = (value: string) => {
  if (value === '1') return '✓';
  if (value === 'beta') return <span className={styles.betaPill}>BETA</span>;
  return '-';
};

const productSortRank = (value: string) => {
  if (value === '1') return 2;
  if (value === 'beta') return 1;
  return 0;
};

const productSort = (field: 'payment' | 'identity' | 'data') => (rowA, rowB) =>
  productSortRank(rowA.original[`${field}_raw`]) -
  productSortRank(rowB.original[`${field}_raw`]);

const CoverageExplorer: React.FC = () => {
  const [csvData, setCsvData] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string>('GB');
  const [selectedSolution, setSelectedSolution] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [csvFileDate, setCsvFileDate] = useState<string>('');

  // Fetch CSV data on component mount
  React.useEffect(() => {
    const prefix = window.location.pathname.split('/docs')[0];
    fetch(`${prefix}/documents/eu_institution_coverage.csv`)
      .then((response) => response.text())
      .then((data) => {
        // Extract timestamp from CSV comment lines
        const lines = data.split('\n');
        const timestampLine = lines.find((line) =>
          line.startsWith('# Generated on:'),
        );
        if (timestampLine) {
          const timestamp = timestampLine
            .replace('# Generated on: ', '')
            .trim();
          const date = new Date(timestamp);
          setCsvFileDate(
            date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          );
        }

        setCsvData(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error loading CSV data:', error);
        setIsLoading(false);
      });
  }, []);

  const brands = useMemo(() => (csvData ? parseCSV(csvData) : []), [csvData]);

  const countryOptions = useMemo(() => {
    const uniqueCountries = [
      ...new Set(brands.map((brand) => brand.country_code)),
    ];
    return uniqueCountries
      .filter((code) => code in COUNTRY_NAMES)
      .map((code) => ({
        label: `${COUNTRY_FLAGS[code]} ${COUNTRY_NAMES[code]} (${code})`,
        value: code,
      }))
      .sort((a, b) =>
        COUNTRY_NAMES[a.value].localeCompare(COUNTRY_NAMES[b.value]),
      );
  }, [brands]);

  const filteredBrands = useMemo(() => {
    let filtered = brands;

    // Filter by country
    filtered = filtered.filter(
      (brand) => brand.country_code === selectedCountry,
    );

    // Filter by solution (beta counts as supported for filtering purposes)
    if (selectedSolution !== 'all') {
      filtered = filtered.filter((brand) => {
        const value = brand[selectedSolution as Solution];
        return value === '1' || value === 'beta';
      });
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (brand) =>
          brand.brand_name.toLowerCase().includes(query) ||
          brand.institutions.some((inst) =>
            inst.institution_name.toLowerCase().includes(query),
          ),
      );
    }

    return filtered;
  }, [brands, selectedCountry, selectedSolution, searchQuery]);

  const columns = useMemo(
    () => [
      {
        Header: 'Bank',
        accessor: 'brand_header',
        setWidth: '400px',
        sortType: (rowA, rowB) => {
          const a = rowA.original.brand_name.toLowerCase().trim();
          const b = rowB.original.brand_name.toLowerCase().trim();
          return a.localeCompare(b, undefined, { sensitivity: 'base' });
        },
      },
      {
        Header: (
          <Tooltip placement='top' showOn='hover' target={<div>Payments</div>}>
            <div className={styles.productCardWrapper}>
              <a className={styles.card}>
                <Image
                  className={styles.icon}
                  src='/assets/img/navbar/icon-payment-initiation-v3.png'
                  alt=''
                  isLazy={false}
                />
                <div className={styles.text}>
                  <h4 className={styles.productCardTitle}>
                    Payment Initiation
                  </h4>
                  <p className={styles.productCardSubtitle}>
                    Initiate payments within your app.
                  </p>
                </div>
              </a>
            </div>
          </Tooltip>
        ),
        accessor: 'payment',
        setWidth: '80px',
        align: 'center',
        sortType: productSort('payment'),
      },
      {
        Header: (
          <Tooltip placement='top' showOn='hover' target={<div>Identity</div>}>
            <div className={styles.productCardWrapper}>
              <a className={styles.card}>
                <Image
                  className={styles.icon}
                  src='/assets/img/navbar/icon-identity-v3.png'
                  alt=''
                  isLazy={false}
                />
                <div className={styles.text}>
                  <h4 className={styles.productCardTitle}>Identity</h4>
                  <p className={styles.productCardSubtitle}>
                    Verify users' financial account ownership and reduce fraud.
                  </p>
                </div>
              </a>
            </div>
          </Tooltip>
        ),
        accessor: 'identity',
        setWidth: '80px',
        align: 'center',
        sortType: productSort('identity'),
      },
      {
        Header: (
          <Tooltip
            placement='top'
            showOn='hover'
            target={<div>Account Data</div>}
          >
            <div className={styles.productCardWrapper}>
              <a className={styles.card}>
                <Image
                  className={styles.icon}
                  src='/assets/img/navbar/icon-transactions-v3.png'
                  alt=''
                  isLazy={false}
                />
                <div className={styles.text}>
                  <h4 className={styles.productCardTitle}>Data</h4>
                  <p className={styles.productCardSubtitle}>
                    Access users' financial transaction data for smarter credit
                    and lending decisions.
                  </p>
                </div>
              </a>
            </div>
          </Tooltip>
        ),
        accessor: 'data',
        setWidth: '200px',
        align: 'center',
        sortType: productSort('data'),
      },
      {
        Header: 'Account Type',
        accessor: 'account_type_header',
        setWidth: '220px',
        sortType: (rowA, rowB) => {
          const typeA = rowA.original.account_type;
          const typeB = rowB.original.account_type;
          return (
            accountTypeSortingOrder[typeA] - accountTypeSortingOrder[typeB]
          );
        },
      },
    ],
    [],
  );

  const rows = useMemo(
    () =>
      filteredBrands.map((brand) => {
        let accountType = '';
        if (brand.personal === '1' && brand.business === '1') {
          accountType = 'Personal / Business';
        } else if (brand.personal === '1') {
          accountType = 'Personal';
        } else if (brand.business === '1') {
          accountType = 'Business';
        } else {
          accountType = '-';
        }

        return {
          ...brand,
          brand_header: (
            <Box display='flex' alignItems='center'>
              <b>{brand.brand_name}</b>
              {brand.institutions.length > 1 && (
                <Tooltip
                  placement='top'
                  showOn='hover'
                  target={
                    <div className={styles.institutionCount}>
                      {brand.institutions.length}
                    </div>
                  }
                >
                  Multiple institutions are available for this bank.
                </Tooltip>
              )}
            </Box>
          ),
          payment: renderProductCell(brand.payment),
          identity: renderProductCell(brand.identity),
          data: renderProductCell(brand.data),
          payment_raw: brand.payment,
          identity_raw: brand.identity,
          data_raw: brand.data,
          account_type_header: <b>{accountType}</b>,
          account_type: accountType,
        };
      }),
    [filteredBrands],
  );

  return (
    <div className={styles.container}>
      <Box className={styles.filterGrid}>
        <Box>
          <Box color='secondary' marginBottom={1} fontWeight={600}>
            Available Markets
          </Box>
          <Select
            id='countryFilter'
            label=''
            name='countrySelect'
            onChange={(e) => {
              setSelectedCountry(e.value);
              setSearchQuery('');
            }}
            options={countryOptions}
            defaultValue={{
              label: '\u{1F1EC}\u{1F1E7} United Kingdom (GB)',
              value: 'GB',
            }}
          />
        </Box>
        <Box>
          <Box color='secondary' marginBottom={1} fontWeight={600}>
            Solutions
          </Box>
          <Select
            id='solutionFilter'
            label=''
            name='solutionSelect'
            onChange={(e) => {
              setSelectedSolution(e.value);
            }}
            options={SOLUTION_OPTIONS}
            defaultValue={SOLUTION_OPTIONS[0]}
          />
        </Box>
      </Box>
      <ThreadsTableGroup
        isLoading={isLoading}
        columns={columns}
        className={styles.table}
        rows={rows}
        label=''
        hasPagination
        pagination={{
          rowsPerPage: 50,
        }}
        initialState={{
          sortBy: [
            {
              id: 'brand_header',
              desc: false,
            },
          ],
        }}
        search={
          <ThreadsSearchInput
            className='search-input'
            classes={{}}
            state='default'
            id='bank-search'
            label='Search banks'
            onChange={(e) => setSearchQuery(e.target.value)}
            onClearButtonClick={() => setSearchQuery('')}
            placeholder='Search banks by name'
            value={searchQuery}
            inputProps={{ spellCheck: false }}
            size='48'
            sx={{
              fontSize: '1.6rem',
              margin: '0',
              padding: '0',
              '& input': {
                border: 'none',
                padding: '0',
                margin: '0',
                '&:focus': {
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none',
                },
              },
            }}
          />
        }
      />
      <Box marginBottom={2} marginTop={2}>
        <Box
          color='secondary'
          marginBottom={1}
          marginTop={2}
          fontWeight={400}
          fontSize='1.2rem'
        >
          Last updated: {csvFileDate}
        </Box>
      </Box>
    </div>
  );
};

export default CoverageExplorer;
